import { useState, useEffect, useCallback, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import type { QueryDocumentSnapshot } from "firebase/firestore";
import { formatError } from "../utils/formatError";
import logger from "../utils/logger";
import type { AuditEntry } from "../types";

const PAGE_SIZE = 50;

interface CursorState {
  byUser: QueryDocumentSnapshot<unknown>[];
  byTarget: QueryDocumentSnapshot<unknown>[];
}

/**
 * Fetches audit log entries where the officer is either the actor (userId)
 * or the target (targetUserId). Two Firestore queries are merged and
 * deduplicated, then sorted by timestamp descending.
 */
export function useOfficerAuditLog(enabled = true, agencyId: string | null = null, userId: string | null = null) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [fetching, setFetching] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const cursorsRef = useRef<CursorState>({ byUser: [], byTarget: [] });
  // Disable TS narrowing on the cursor type — Firestore's generics fight us
  // here, but the actual values are always doc snapshots returned from getDocs.

  const depsKey = `${agencyId || ""}:${userId || ""}`;
  const loading = enabled && !!agencyId && !!userId && (loadedFor !== depsKey || fetching);

  const fetchPage = useCallback(async (pageNum: number) => {
    if (!enabled || !agencyId || !userId) return;
    setFetching(true);
    setError(null);
    try {
      const col = collection(db, "auditLog");

      const byUserBase = query(
        col,
        where("agencyId", "==", agencyId),
        where("userId", "==", userId),
        orderBy("timestamp", "desc"),
      );

      const byTargetBase = query(
        col,
        where("agencyId", "==", agencyId),
        where("targetUserId", "==", userId),
        orderBy("timestamp", "desc"),
      );

      const fetchSize = PAGE_SIZE + 1;
      let q1, q2;
      if (pageNum === 0) {
        q1 = query(byUserBase, limit(fetchSize));
        q2 = query(byTargetBase, limit(fetchSize));
      } else {
        const userCursor = cursorsRef.current.byUser[pageNum - 1];
        const targetCursor = cursorsRef.current.byTarget[pageNum - 1];
        q1 = userCursor
          ? query(byUserBase, startAfter(userCursor), limit(fetchSize))
          : query(byUserBase, limit(0));
        q2 = targetCursor
          ? query(byTargetBase, startAfter(targetCursor), limit(fetchSize))
          : query(byTargetBase, limit(0));
      }

      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

      // Merge and deduplicate
      const seen = new Set<string>();
      const all: (AuditEntry & { _doc: QueryDocumentSnapshot })[] = [];
      for (const docSnap of [...snap1.docs, ...snap2.docs]) {
        if (!seen.has(docSnap.id)) {
          seen.add(docSnap.id);
          all.push({ id: docSnap.id, ...(docSnap.data() as object), _doc: docSnap } as AuditEntry & { _doc: QueryDocumentSnapshot });
        }
      }

      all.sort((a, b) => {
        const ta = (a.timestamp as { toDate?: () => Date })?.toDate?.()?.getTime() ?? new Date((a.timestamp as unknown as string | number) || 0).getTime();
        const tb = (b.timestamp as { toDate?: () => Date })?.toDate?.()?.getTime() ?? new Date((b.timestamp as unknown as string | number) || 0).getTime();
        return tb - ta;
      });

      const hasNext = all.length > PAGE_SIZE;
      const pageDocs = all.slice(0, PAGE_SIZE);

      if (snap1.docs.length > 0) {
        const userDocs = snap1.docs.slice(0, PAGE_SIZE);
        cursorsRef.current.byUser[pageNum] = userDocs[userDocs.length - 1];
      }
      if (snap2.docs.length > 0) {
        const targetDocs = snap2.docs.slice(0, PAGE_SIZE);
        cursorsRef.current.byTarget[pageNum] = targetDocs[targetDocs.length - 1];
      }

      // Strip the internal _doc cursor handle before exposing to consumers.
      setEntries(pageDocs.map((d) => {
        const copy = { ...d } as Partial<typeof d>;
        delete copy._doc;
        return copy as AuditEntry;
      }));
      setHasMore(hasNext);
      setPage(pageNum);
    } catch (err) {
      logger.error("Failed to load officer audit log:", err);
      setError(formatError(err));
    } finally {
      setLoadedFor(`${agencyId || ""}:${userId || ""}`);
      setFetching(false);
    }
  }, [enabled, agencyId, userId]);

  useEffect(() => {
    cursorsRef.current = { byUser: [], byTarget: [] };
    Promise.resolve().then(() => fetchPage(0));
  }, [fetchPage]);

  const loadNextPage = useCallback(() => {
    if (hasMore) fetchPage(page + 1);
  }, [hasMore, page, fetchPage]);

  const loadPrevPage = useCallback(() => {
    if (page > 0) {
      cursorsRef.current.byUser = cursorsRef.current.byUser.slice(0, page - 1);
      cursorsRef.current.byTarget = cursorsRef.current.byTarget.slice(0, page - 1);
      fetchPage(page - 1);
    }
  }, [page, fetchPage]);

  const fetchAll = useCallback(async (): Promise<AuditEntry[]> => {
    if (!enabled || !agencyId || !userId) return [];
    const col = collection(db, "auditLog");
    const batchSize = 500;
    const allEntries: AuditEntry[] = [];
    const seen = new Set<string>();

    for (const field of ["userId", "targetUserId"] as const) {
      let lastDoc: QueryDocumentSnapshot<unknown> | null = null;
      while (true) {
        const q = query(
          col,
          where("agencyId", "==", agencyId),
          where(field, "==", userId),
          orderBy("timestamp", "desc"),
          ...(lastDoc ? [startAfter(lastDoc)] : []),
          limit(batchSize),
        );
        const snapshot = await getDocs(q);
        if (snapshot.empty) break;
        for (const docSnap of snapshot.docs) {
          if (!seen.has(docSnap.id)) {
            seen.add(docSnap.id);
            allEntries.push({ id: docSnap.id, ...(docSnap.data() as object) } as AuditEntry);
          }
        }
        if (snapshot.docs.length < batchSize) break;
        lastDoc = snapshot.docs[snapshot.docs.length - 1];
      }
    }

    allEntries.sort((a, b) => {
      const ta = (a.timestamp as { toDate?: () => Date })?.toDate?.()?.getTime() ?? new Date((a.timestamp as unknown as string | number) || 0).getTime();
      const tb = (b.timestamp as { toDate?: () => Date })?.toDate?.()?.getTime() ?? new Date((b.timestamp as unknown as string | number) || 0).getTime();
      return tb - ta;
    });

    return allEntries;
  }, [enabled, agencyId, userId]);

  return {
    log: entries,
    loading,
    error,
    hasMore,
    hasPrev: page > 0,
    page,
    loadNextPage,
    loadPrevPage,
    fetchAll,
  };
}
